import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Smartphone } from "lucide-react";

/**
 * PWA install prompt banner.
 * Listens for `beforeinstallprompt` (Chrome / Edge / Android) and
 * offers a one-tap install button. Also registers the service worker.
 * Dismissal persists for 7 days via localStorage.
 */
const DISMISS_KEY = "mara_pwa_install_dismissed_at";
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Register service worker (once).
    if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
      navigator.serviceWorker.register("/sw.js").catch(() => null);
    } else if ("serviceWorker" in navigator) {
      // In dev, still register so the offline fallback works.
      navigator.serviceWorker.register("/sw.js").catch(() => null);
    }

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* noop */ }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] max-w-md w-[92vw] bg-[#0f1a15] border border-[#F4B400]/40 rounded-2xl shadow-2xl p-4 flex items-start gap-3"
      data-testid="pwa-install-prompt"
    >
      <div className="w-10 h-10 rounded-full bg-[#F4B400]/20 border border-[#F4B400]/40 flex items-center justify-center shrink-0">
        <Smartphone className="w-5 h-5 text-[#F4B400]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-anton text-lg leading-none uppercase tracking-tight text-white">Install Mara League</div>
        <p className="text-xs text-zinc-400 mt-1">
          One-tap access to live scores, standings and schedule. Works offline for pages you've viewed.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" onClick={install} className="bg-[#F4B400] text-black hover:bg-[#e5aa00] font-bold uppercase tracking-widest text-xs" data-testid="pwa-install-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Install
          </Button>
          <button onClick={dismiss} className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white" data-testid="pwa-dismiss-btn">
            Not now
          </button>
        </div>
      </div>
      <button onClick={dismiss} className="text-zinc-500 hover:text-white shrink-0" aria-label="Close">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
